import { Link, useLocation } from "wouter";
import { getStoredUser } from "@/lib/storage";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Plug, KeyRound, Webhook, User, CreditCard, ArrowLeft, Check, Layout, PanelLeft, PanelTop, Camera, Building2, Users, UserPlus, Trash2, Crown, Shield, Eye, Send, Loader2, Key, Palette, Lock, Globe, PenTool, Type, FileCheck, Clock, Pencil, Mail, Plus, X, Code2, MessageCircle } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useState, useEffect, useRef } from "react";
import { useSettings } from "@/components/settings-provider";
import { useLanguage } from "@/components/language-provider";
import { AppLayout } from "@/components/app-layout";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { countries } from "@/lib/countries";
import { getWebsiteUrl, getApiUrl } from "@/lib/config";

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};
import { planLabel } from "@/lib/plan";
import { useCrmContext } from "@/lib/crm/api";
import { crmText } from "@/lib/crm/i18n";
import { setCompanyCrm } from "@/components/crm/crm-shell";
import { useConfirm } from "@/components/crm/crm-ui";
import { VerificationQrSetting, type VerificationQrMode } from "@/components/verification-qr-setting";
import { SigningDeadlineSetting } from "@/components/signing-deadline-setting";
import { RenewalControl } from "@/components/renewal-control";

type CompanyRole = "owner" | "admin" | "sender" | "viewer";

interface CompanyMember {
  id: string;
  userId: string;
  email: string;
  name: string;
  role: CompanyRole;
  status: string;
  joinedAt: string;
}

interface Company {
  id: string;
  name: string;
  ownerId: string;
  plan: string;
  members: CompanyMember[];
  signatureExpirationDays?: number;
  requireCpf?: boolean;
  enableGovBrManagement?: boolean;
  [key: string]: unknown;
}

interface EmailTemplate {
  id: string;
  companyId: string | null;
  userId: string;
  templateType: "signature_request" | "signature_request_govbr" | "signature_completed" | "signature_reminder";
  name: string;
  subject: string;
  bodyHtml: string;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

function SuperAdminSection() {
  const [, navigate] = useLocation();
  const { t } = useLanguage();
  return (
    <Card className="animate-fade-in-up stagger-7 border-amber-500/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-amber-500" /> {t("settingsx.superAdmin")}
        </CardTitle>
        <CardDescription>{t("ui.platform_administration_tools")}</CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={() => navigate("/admin")} className="w-full">
          <Shield className="mr-2 h-4 w-4" /> {t("ui.open_admin_portal")}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const [, navigateTo] = useLocation();
  const [, setLocation] = useLocation();
  const [user, setUser] = useState<any>(null);
  const { sidebarPlacement, setSidebarPlacement, colorPalette, setColorPalette } = useSettings();
  const { t, language: langFromContext } = useLanguage();
  const language = langFromContext || "en";
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const newLogoInputRef = useRef<HTMLInputElement>(null);
  const companyCardRef = useRef<HTMLDivElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [companyLogo, setCompanyLogo] = useState<string | null>(null);
  const [highlightCompany, setHighlightCompany] = useState(false);
  // CRM module (Clientes e Leads): one switch for the whole company, shown to company admins only.
  const crmContext = useCrmContext(!!user);
  const crmCardRef = useRef<HTMLDivElement>(null);
  const [highlightCrm, setHighlightCrm] = useState(false);
  const [highlightPlans, setHighlightPlans] = useState(false);
  const plansCardRef = useRef<HTMLDivElement>(null);
  const [crmSaving, setCrmSaving] = useState(false);
  const [crmConfirm, askCrm] = useConfirm();
  useEffect(() => {
    if (highlightCrm && crmContext.data) crmCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightCrm, crmContext.data]);
  const toggleCrmModule = async (enabled: boolean) => {
    const lang = language === "pt" ? "pt" : "en";
    if (!enabled) {
      const ok = await askCrm({
        title: crmText(lang, "disableModule"), text: crmText(lang, "confirmDisableModule"),
        confirm: crmText(lang, "deactivate"), cancel: crmText(lang, "cancel"), destructive: true,
      });
      if (!ok) return;
    }
    setCrmSaving(true);
    try {
      await setCompanyCrm(enabled);
      toast({ title: crmText(lang, enabled ? "crmEnabled" : "crmDisabled") });
    } catch (e: any) {
      toast({ title: t("common.error"), description: e?.message, variant: "destructive" });
    } finally {
      setCrmSaving(false);
    }
  };
  
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [showInviteMember, setShowInviteMember] = useState(false);
  const [showEditCompany, setShowEditCompany] = useState(false);
  const [showMigrateDocuments, setShowMigrateDocuments] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [companyCnpj, setCompanyCnpj] = useState("");
  const [companyCountry, setCompanyCountry] = useState<"brazil" | "international">("international");
  // Auto-detect country from user registration
  useEffect(() => { if (user?.countryCode === "BR") setCompanyCountry("brazil"); }, [user]);
  const [newCompanyLogoFile, setNewCompanyLogoFile] = useState<File | null>(null);
  const [newCompanyLogoPreview, setNewCompanyLogoPreview] = useState<string | null>(null);
  const [editCompanyName, setEditCompanyName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<CompanyRole>("viewer");
  const [selectedDocsToMigrate, setSelectedDocsToMigrate] = useState<string[]>([]);
  
  const [signatureCountry, setSignatureCountry] = useState<"brazil" | "international">("international");
  const [allowedMethods, setAllowedMethods] = useState<string[]>(["draw", "govbr", "typed"]);
  const [defaultMethod, setDefaultMethod] = useState<"draw" | "govbr" | "typed">("draw");
  const [requireCpf, setRequireCpf] = useState(false);
  const [enableGovBrManagement, setEnableGovBrManagement] = useState(false);
  const [verificationQrMode, setVerificationQrMode] = useState<VerificationQrMode>("every_page");
  // Documents sent outside a company: the person's own choice (GET/PUT /api/account/settings).
  const [personalQrMode, setPersonalQrMode] = useState<VerificationQrMode | null>(null);
  const [whatsAppResendEnabled, setWhatsAppResendEnabled] = useState(true);
  const [signatureExpirationDays, setSignatureExpirationDays] = useState(7);

  // SSO state
  const [ssoEnabled, setSsoEnabled] = useState(false);
  const [ssoProvider, setSsoProvider] = useState("");
  const [azureTenantId, setAzureTenantId] = useState("");
  const [azureClientId, setAzureClientId] = useState("");
  const [samlEntityId, setSamlEntityId] = useState("");
  const [samlSignOnUrl, setSamlSignOnUrl] = useState("");

  const [showEmailTemplateEditor, setShowEmailTemplateEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [templateType, setTemplateType] = useState<EmailTemplate["templateType"]>("signature_request");
  const [templateSubject, setTemplateSubject] = useState("");
  const [templateBody, setTemplateBody] = useState("");
  const [templateIsDefault, setTemplateIsDefault] = useState(false);

  useEffect(() => {
    const u = getStoredUser();
    if (!u) {
      setLocation("/login");
      return;
    }
    setUser(u);
  }, [setLocation]);

  // Handle URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    
    // Handle subscription success (after Stripe redirect or test mode)
    const subStatus = params.get("subscription");
    if (subStatus === "success") {
      const plan = params.get("plan") || "";
      const sessionId = params.get("session_id");
      window.history.replaceState({}, "", window.location.pathname);
      const applyPlan = (p: string) => {
        const u = getStoredUser<any>();
        if (u && p) {
          u.plan = p;
          localStorage.setItem("user", JSON.stringify(u));
          setUser(u);
        }
        queryClient.invalidateQueries({ queryKey: ["/api/v1/plans"] });
        queryClient.invalidateQueries({ queryKey: ["/api/companies/my"] });
        queryClient.invalidateQueries({ queryKey: ["/api/documents/check-limit"] });
      };
      const planName = (p: string) => p.charAt(0).toUpperCase() + p.slice(1);
      if (sessionId) {
        // Real Stripe checkout: activate from the session now (the webhook may arrive later or not at all).
        (async () => {
          try {
            const res = await fetch(getApiUrl("/api/v1/subscriptions/confirm-checkout"), {
              method: "POST",
              headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
              body: JSON.stringify({ sessionId }),
            });
            const data = res.ok ? await res.json() : null;
            if (data?.activated) {
              applyPlan(data.plan || plan);
              toast({
                title: language === "pt" ? "Pagamento confirmado!" : "Payment confirmed!",
                description: language === "pt"
                  ? `Seu plano agora é ${planName(data.plan || plan)}. Bom trabalho!`
                  : `Your plan is now ${planName(data.plan || plan)}. Enjoy!`,
              });
            } else {
              toast({
                title: language === "pt" ? "Pagamento recebido" : "Payment received",
                description: language === "pt"
                  ? "Seu plano é atualizado em instantes. Se não mudar em alguns minutos, fale com o suporte."
                  : "Your plan will update in a moment. If it doesn't change within a few minutes, contact support.",
              });
            }
          } catch {
            toast({ title: language === "pt" ? "Pagamento recebido" : "Payment received" });
          }
        })();
      } else {
        toast({
          title: language === "pt" ? "Plano atualizado!" : "Plan updated!",
          description: language === "pt"
            ? `Seu plano foi atualizado para ${planName(plan)} com sucesso.`
            : `Your plan has been updated to ${planName(plan)} successfully.`,
        });
        applyPlan(plan);
      }
    }

    if (params.get("section") === "crm") {
      window.history.replaceState({}, "", window.location.pathname);
      setHighlightCrm(true);
      setTimeout(() => setHighlightCrm(false), 4000);
    }

    if (params.get("action") === "plans") {
      // From a "see plans" button: bring the plans into view and highlight them for a moment.
      window.history.replaceState({}, "", window.location.pathname);
      setHighlightPlans(true);
      setTimeout(() => plansCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
      setTimeout(() => setHighlightPlans(false), 4000);
    }

    if (params.get("action") === "create-company") {
      setHighlightCompany(true);
      // Remove param from URL without reload
      window.history.replaceState({}, "", window.location.pathname);
      setTimeout(() => {
        companyCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        // If no company yet, auto-open the create dialog
        setTimeout(() => {
          setShowCreateCompany(true);
        }, 600);
      }, 300);
      // Remove highlight after a few seconds
      setTimeout(() => setHighlightCompany(false), 4000);
    }
  }, [user]);

  const { data: companyData, isLoading: companyLoading } = useQuery<Company | null>({
    queryKey: ["/api/companies/my"],
    enabled: !!user && (user.isSuperAdmin || user.plan !== "free"),
  });

  // No company: the QR choice for the person's own documents.
  useEffect(() => {
    if (!user || companyLoading || companyData) return;
    let alive = true;
    apiRequest("GET", "/api/account/settings")
      .then((r) => r.json())
      .then((d) => { if (alive && d?.verificationQrMode) setPersonalQrMode(d.verificationQrMode as VerificationQrMode); })
      .catch(() => {});
    return () => { alive = false; };
  }, [user, companyLoading, companyData]);

  // Fetch plans from API (same data admin edits)
  const { data: apiPlansData } = useQuery<any[]>({
    queryKey: ["/api/v1/plans"],
    enabled: !!user,
  });

  interface PersonalDoc {
    id: string;
    name: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  }

  const { data: personalDocsData, refetch: refetchPersonalDocs } = useQuery<PersonalDoc[]>({
    queryKey: ["/api/documents/personal"],
    enabled: !!user && !!companyData,
  });

  const { data: emailTemplates, isLoading: templatesLoading } = useQuery<EmailTemplate[]>({
    queryKey: ["/api/email-templates"],
    enabled: !!user,
  });

  const createTemplateMutation = useMutation({
    mutationFn: async (data: { templateType: string; name: string; subject: string; bodyHtml: string; isDefault: boolean }) => {
      const res = await apiRequest("POST", "/api/email-templates", data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to create template");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-templates"] });
      setShowEmailTemplateEditor(false);
      resetTemplateForm();
      toast({ title: t("common.success"), description: t("settingsx.templateCreated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const updateTemplateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { name?: string; subject?: string; bodyHtml?: string; isDefault?: boolean; isActive?: boolean } }) => {
      const res = await apiRequest("PUT", `/api/email-templates/${id}`, data);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to update template");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-templates"] });
      setShowEmailTemplateEditor(false);
      setEditingTemplate(null);
      resetTemplateForm();
      toast({ title: t("common.success"), description: t("settingsx.templateUpdated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await apiRequest("DELETE", `/api/email-templates/${id}`);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to delete template");
      }
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/email-templates"] });
      toast({ title: t("common.success"), description: t("settingsx.templateDeleted") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const resetTemplateForm = () => {
    setTemplateName("");
    setTemplateType("signature_request");
    setTemplateSubject("");
    setTemplateBody("");
    setTemplateIsDefault(false);
  };

  const openEditTemplate = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setTemplateName(template.name);
    setTemplateType(template.templateType);
    setTemplateSubject(template.subject);
    setTemplateBody(template.bodyHtml);
    setTemplateIsDefault(template.isDefault);
    setShowEmailTemplateEditor(true);
  };

  const handleSaveTemplate = () => {
    if (!templateName || !templateSubject || !templateBody) {
      toast({ title: t("common.error"), description: t("ui.please_fill_in_all_required_fields"), variant: "destructive" });
      return;
    }
    if (editingTemplate) {
      updateTemplateMutation.mutate({
        id: editingTemplate.id,
        data: { name: templateName, subject: templateSubject, bodyHtml: templateBody, isDefault: templateIsDefault },
      });
    } else {
      createTemplateMutation.mutate({
        templateType,
        name: templateName,
        subject: templateSubject,
        bodyHtml: templateBody,
        isDefault: templateIsDefault,
      });
    }
  };

  const getTemplateTypeLabel = (type: string) => {
    switch (type) {
      case "signature_request": return "Signature Request";
      case "signature_request_govbr": return "GOV.BR Signature Request";
      case "signature_completed": return "Signature Completed";
      case "signature_reminder": return "Signature Reminder";
      default: return type;
    }
  };

  const migrateDocumentsMutation = useMutation({
    mutationFn: async (documentIds: string[]) => {
      const res = await apiRequest("POST", "/api/documents/migrate", { documentIds });
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/documents"] });
      queryClient.invalidateQueries({ queryKey: ["/api/documents/personal"] });
      setShowMigrateDocuments(false);
      setSelectedDocsToMigrate([]);
      toast({ title: t("common.success"), description: data.message });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const createCompanyMutation = useMutation({
    mutationFn: async (name: string) => {
      const slug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") + "-" + Date.now().toString(36);
      const res = await apiRequest("POST", "/api/companies", {
        name,
        slug,
        contactEmail: user?.email || "",
        country: companyCountry === "brazil" ? "brazil" : "international",
      });
      return res.json();
    },
    onSuccess: async (data: any) => {
      const companyId = data?.id || data?.data?.id;
      if (companyId && user) {
        const updatedUser = { ...user, companyId };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setUser(updatedUser);
      }

      queryClient.invalidateQueries({ queryKey: ["/api/companies/my"] });
      queryClient.invalidateQueries({ queryKey: ["/api/companies/list"] });
      setShowCreateCompany(false);
      setCompanyName("");
      setCompanyCnpj("");
      setCompanyCountry("international");
      setNewCompanyLogoFile(null);
      setNewCompanyLogoPreview(null);
      setHighlightCompany(false);
      toast({ title: t("common.success"), description: t("settingsx.companyCreated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const inviteMemberMutation = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: CompanyRole }) => {
      const res = await apiRequest("POST", `/api/companies/${companyData?.id}/invite`, { email, role });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies/my"] });
      setShowInviteMember(false);
      setInviteEmail("");
      setInviteRole("viewer");
      toast({ title: t("common.success"), description: t("settingsx.memberInvited") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const res = await apiRequest("DELETE", `/api/companies/${companyData?.id}/members/${memberId}`);
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to remove member");
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies/my"] });
      toast({ title: t("common.success"), description: t("settingsx.memberRemoved") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: string; role: CompanyRole }) => {
      const res = await apiRequest("PATCH", `/api/companies/${companyData?.id}/members/${memberId}/role`, { role });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Failed to update role");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies/my"] });
      toast({ title: t("common.success"), description: t("settingsx.roleUpdated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const updateCompanyMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const res = await apiRequest("PATCH", "/api/company", data);
      const text = await res.text();
      return text ? JSON.parse(text) : null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies/my"] });
      setShowEditCompany(false);
      toast({ title: t("common.success"), description: t("settingsx.companyUpdated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  const updateSignaturePolicyMutation = useMutation({
    mutationFn: async (data: Record<string, any>) => {
      const res = await apiRequest("PATCH", "/api/company", data);
      const text = await res.text();
      return text ? JSON.parse(text) : null;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies/my"] });
      toast({ title: t("common.success"), description: t("settingsx.settingsUpdated") });
    },
    onError: (error: Error) => {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    },
  });

  useEffect(() => {
    if (companyData) {
      const company = companyData as any;
      setSignatureCountry(company.country || "international");
      setAllowedMethods((company.allowedSignatureMethods || "draw,govbr,typed").split(","));
      setDefaultMethod(company.defaultSignatureMethod || "draw");
      setRequireCpf(company.requireCpf || false);
      setEnableGovBrManagement(company.enableGovBrManagement || false);
      setWhatsAppResendEnabled(company.whatsAppResendEnabled !== false);
      setVerificationQrMode((company.verificationQrMode as VerificationQrMode) || "every_page");
      // 0 = no deadline
      setSignatureExpirationDays(typeof company.signatureExpirationDays === "number" ? company.signatureExpirationDays : 7);
      setCompanyLogo(company.logoUrl || null);
      // Load SSO settings
      setSsoEnabled(company.ssoEnabled || false);
      setSsoProvider(company.ssoProvider || "");
      setAzureTenantId(company.azureTenantId || "");
      setAzureClientId(company.azureClientId || "");
      setSamlEntityId(company.samlEntityId || "");
      setSamlSignOnUrl(company.samlSignOnUrl || "");
    }
  }, [companyData]);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("logo", file);
      
      const token = localStorage.getItem("token");
      const res = await fetch(getApiUrl("/api/company/logo"), {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Upload failed");
      }
      
      const data = await res.json();
      setCompanyLogo(data.logoUrl);
      queryClient.invalidateQueries({ queryKey: ["/api/companies/my"] });
      toast({ title: t("settingsx.logoUploaded") });
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message, variant: "destructive" });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleMethodToggle = (method: string, checked: boolean) => {
    let newMethods: string[];
    if (checked) {
      newMethods = [...allowedMethods, method];
    } else {
      if (allowedMethods.length <= 1) {
        toast({ title: t("common.error"), description: t("settingsx.oneMethod"), variant: "destructive" });
        return;
      }
      newMethods = allowedMethods.filter(m => m !== method);
      if (defaultMethod === method) {
        setDefaultMethod(newMethods[0] as any);
        updateSignaturePolicyMutation.mutate({ 
          allowedSignatureMethods: newMethods.join(","),
          defaultSignatureMethod: newMethods[0]
        });
        setAllowedMethods(newMethods);
        return;
      }
    }
    setAllowedMethods(newMethods);
    updateSignaturePolicyMutation.mutate({ allowedSignatureMethods: newMethods.join(",") });
  };

  const handleDefaultMethodChange = (method: string) => {
    if (!allowedMethods.includes(method)) {
      toast({ title: t("common.error"), description: t("settingsx.disabledDefault"), variant: "destructive" });
      return;
    }
    setDefaultMethod(method as any);
    updateSignaturePolicyMutation.mutate({ defaultSignatureMethod: method });
  };

  const handleCountryChange = (country: "brazil" | "international") => {
    setSignatureCountry(country);
    const payload: any = { country };
    if (country === "brazil" && !requireCpf) {
      setRequireCpf(true);
      payload.requireCpf = true;
    }
    updateSignaturePolicyMutation.mutate(payload);
  };

  const handleRequireCpfChange = (checked: boolean) => {
    setRequireCpf(checked);
    updateSignaturePolicyMutation.mutate({ requireCpf: checked });
  };

  const handleExpirationDaysChange = (days: number) => {
    setSignatureExpirationDays(days);
    updateSignaturePolicyMutation.mutate({ signatureExpirationDays: days });
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setLocation("/");
  };

  const handleProfilePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      return;
    }
    
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const imageUrl = reader.result as string;
      const updatedUser = { ...user, profileImageUrl: imageUrl };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));
    };
    reader.readAsDataURL(file);
  };

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user?.firstName) {
      return user.firstName.substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return "U";
  };

  const getRoleIcon = (role: CompanyRole) => {
    switch (role) {
      case "owner": return <Crown className="h-4 w-4 text-yellow-500" />;
      case "admin": return <Shield className="h-4 w-4 text-blue-500" />;
      case "sender": return <Send className="h-4 w-4 text-green-500" />;
      case "viewer": return <Eye className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getRoleBadgeVariant = (role: CompanyRole): "default" | "secondary" | "outline" => {
    switch (role) {
      case "owner": return "default";
      case "admin": return "secondary";
      default: return "outline";
    }
  };

  const canManageMembers = companyData && (
    companyData.ownerId === user?.id ||
    companyData.members?.some((m: CompanyMember) => m.userId === user?.id && (m.role === "owner" || m.role === "admin"))
  );

  // Contact-sales dialog state. Must be declared BEFORE the `if (!user) return null`
  // early return below: hooks after a conditional return change the hook count between
  // renders (user is null on the first render) -> React #310 "Rendered more hooks" ->
  // the /settings crash.
  const [salesDialogOpen, setSalesDialogOpen] = useState(false);
  const [salesEmail, setSalesEmail] = useState("");
  const [salesMessage, setSalesMessage] = useState("");
  const [salesSending, setSalesSending] = useState(false);
  useEffect(() => { if (user?.email) setSalesEmail(user.email); }, [user?.email]);

  // Mensal/Anual: the annual option exists only when the admin filled the annual price (Admin → Planos).
  const [billingCycle, setBillingCycle] = useState<"monthly" | "annual">(() =>
    new URLSearchParams(window.location.search).get("cycle") === "annual" ? "annual" : "monthly");
  const { data: currentSubscription } = useQuery<any>({ queryKey: ["/api/v1/subscriptions/current"], enabled: !!user && user.plan !== "free" });

  if (!user) return null;

  const planOrder: Record<string, number> = { free: 0, pro: 1, enterprise: 2, custom: 3, customenterprise: 3, custom_enterprise: 3 };
  const currentPlanLevel = planOrder[user.plan] ?? 0;

  // Build plans from API data or fallback
  const plans = (apiPlansData || []).map((p: any) => {
    let features: { pt: string; en: string; included: boolean }[] = [];
    try { features = JSON.parse(p.features || "[]"); } catch {}
    const planId = (p.planType || "").toLowerCase();
    const monthlyAmount = Number(language === "pt" ? p.priceMonthlyBrl : p.priceMonthlyUsd) || 0;
    const annualAmount = Number(language === "pt" ? p.priceAnnualBrl : p.priceAnnualUsd) || 0;
    const currency = (n: number) => language === "pt" ? `R$ ${n.toLocaleString("pt-BR", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}` : `$${n.toLocaleString("en-US", { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;
    const annualChosen = billingCycle === "annual" && annualAmount > 0 && !p.contactSales;
    const savings = monthlyAmount > 0 && annualAmount > 0 ? Math.max(0, Math.round((1 - annualAmount / (monthlyAmount * 12)) * 100)) : 0;
    return {
      id: planId,
      name: p.name,
      level: planOrder[planId] ?? 0,
      price: p.contactSales
        ? (language === "pt" ? "Sob consulta" : "Contact us")
        : annualChosen ? currency(annualAmount) : currency(monthlyAmount),
      period: p.contactSales ? "" : annualChosen ? (language === "pt" ? "/ano" : "/year") : (language === "pt" ? "/mês" : "/month"),
      perMonth: annualChosen ? (language === "pt" ? `≈ ${currency(Math.round(annualAmount / 12 * 100) / 100)}/mês` : `≈ ${currency(Math.round(annualAmount / 12 * 100) / 100)}/month`) : "",
      annualAvailable: annualAmount > 0 && !p.contactSales,
      savings,
      features: features.map((f: any) => language === "pt" ? f.pt : f.en),
      featuresIncluded: features.map((f: any) => f.included),
      current: user.plan === planId || (planId === "customenterprise" && (user.plan === "custom" || user.plan === "custom_enterprise" || user.plan === "customenterprise")),
      contactSales: p.contactSales,
      isActive: p.isActive !== false,
    };
  });

  // Fallback if API not loaded yet
  if (plans.length === 0) {
    plans.push(
      { id: "free", name: "Free", level: 0, price: "R$ 0", period: "/mês", perMonth: "", annualAvailable: false, savings: 0, features: ["3 docs/mês", "1 usuário"], featuresIncluded: [true, true], current: user.plan === "free", contactSales: false, isActive: true },
      { id: "pro", name: "Professional", level: 1, price: "R$ 199", period: "/mês", perMonth: "", annualAvailable: false, savings: 0, features: ["100 docs/mês", "10 usuários", "API"], featuresIncluded: [true, true, true], current: user.plan === "pro", contactSales: false, isActive: true },
      { id: "enterprise", name: "Enterprise", level: 2, price: "R$ 599", period: "/mês", perMonth: "", annualAvailable: false, savings: 0, features: ["300 docs/mês", "25 usuários", "D365"], featuresIncluded: [true, true, true], current: user.plan === "enterprise", contactSales: false, isActive: true },
    );
  }

  const hasAnnual = plans.some((p) => p.annualAvailable);
  const maxSavings = Math.max(0, ...plans.map((p) => p.savings || 0));
  const currentCycle: "monthly" | "annual" = currentSubscription?.billingCycle === "annual" ? "annual" : "monthly";

  const handleSendSalesContact = async () => {
    if (!salesEmail.trim() || !salesMessage.trim()) {
      toast({ title: language === "pt" ? "Preencha e-mail e mensagem" : "Fill in email and message", variant: "destructive" });
      return;
    }
    setSalesSending(true);
    try {
      const res = await apiRequest("POST", "/api/v1/contact-sales", { email: salesEmail, message: salesMessage });
      if (!res.ok) throw new Error("send failed");
      toast({
        title: language === "pt" ? "Mensagem enviada!" : "Message sent!",
        description: language === "pt"
          ? "Nossa equipe vai analisar e retornar no e-mail informado em até 1 dia útil."
          : "Our team will review it and reply within 1 business day.",
      });
      setSalesDialogOpen(false);
      setSalesMessage("");
    } catch {
      toast({ title: language === "pt" ? "Erro ao enviar. Tente novamente." : "Failed to send. Try again.", variant: "destructive" });
    } finally {
      setSalesSending(false);
    }
  };

  const handleUpgrade = async (planId: string, cycle: "monthly" | "annual" = "monthly") => {
    if (planId === "custom" || planId === "customenterprise") {
      window.location.href = getWebsiteUrl("/enterprise-contact?plan=custom");
      return;
    }

    try {
      const res = await fetch(getApiUrl("/api/v1/subscriptions/create-checkout-session"), {
        method: "POST",
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({
          planId,
          billingCycle: cycle,
          successUrl: `${window.location.origin}/settings?subscription=success&plan=${planId}&cycle=${cycle}`,
          cancelUrl: `${window.location.origin}/settings?subscription=cancelled`,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        // The server says what is wrong (payment not configured, Stripe refused the price, ...).
        const detail = language === "pt" ? data.messagePt : data.messageEn;
        toast({
          title: language === "pt" ? "Não foi possível abrir o pagamento" : "Could not open the payment page",
          description: (detail || data.message || "") + (user?.isSuperAdmin && data.message ? ` (${data.message})` : ""),
          variant: "destructive",
        });
        return;
      }
      const url = data.checkoutUrl || data.data?.checkoutUrl;
      if (url) {
        // If URL points back to settings it means Stripe is in test mode
        if (url.includes("/settings?subscription=success")) {
          toast({
            title: language === "pt" ? "Modo de teste" : "Test mode",
            description: language === "pt"
              ? "Stripe em modo teste. Plano ativado sem pagamento. Configure os Price IDs no painel de admin para habilitar pagamentos reais."
              : "Stripe in test mode. Plan activated without payment. Configure Price IDs in the admin panel to enable real payments.",
          });
          // Reload user data
          setTimeout(() => {
            const stored = localStorage.getItem("user");
            if (stored) {
              const u = JSON.parse(stored);
              u.plan = planId;
              localStorage.setItem("user", JSON.stringify(u));
              setUser(u);
            }
            queryClient.invalidateQueries({ queryKey: ["/api/v1/plans"] });
            queryClient.invalidateQueries({ queryKey: ["/api/companies/my"] });
          }, 500);
        } else {
          window.location.href = url;
        }
      } else {
        toast({ title: t("common.error"), description: t("settingsx.checkoutFailed"), variant: "destructive" });
      }
    } catch (error: any) {
      toast({ title: t("common.error"), description: error.message || "Failed to start checkout", variant: "destructive" });
    }
  };

  return (
    <AppLayout user={user} onLogout={handleLogout} currentPage="/settings">
        <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <Link href="/dashboard">
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" /> {t("ui.back_to_dashboard")}
            </Button>
          </Link>
          <h1 className="text-2xl font-semibold">{t("ui.settings")}</h1>
          <p className="text-muted-foreground">{t("settingsx.subtitle")}</p>
        </div>

        <div className="space-y-6">
          <Card className="animate-fade-in-up">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layout className="h-5 w-5" /> {t("settings.appearance")}
              </CardTitle>
              <CardDescription>{t("settings.appearance.desc")}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-3">
                <Label>{t("settings.sidebar")}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setSidebarPlacement("top")}
                    className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-smooth ${
                      sidebarPlacement === "top" ? "border-primary bg-primary/10" : "border-border"
                    }`}
                    data-testid="button-sidebar-top"
                  >
                    <PanelTop className="h-6 w-6" />
                    <span className="text-sm font-medium">{t("settings.sidebar.top")}</span>
                  </button>
                  <button
                    onClick={() => setSidebarPlacement("left")}
                    className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-smooth ${
                      sidebarPlacement === "left" ? "border-primary bg-primary/10" : "border-border"
                    }`}
                    data-testid="button-sidebar-left"
                  >
                    <PanelLeft className="h-6 w-6" />
                    <span className="text-sm font-medium">{t("settings.sidebar.left")}</span>
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                <Label>{t("settings.palette")}</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setColorPalette("brand")}
                    className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-smooth ${
                      colorPalette === "brand" ? "border-primary bg-primary/10" : "border-border"
                    }`}
                    data-testid="button-palette-brand"
                  >
                    <div className="flex gap-1">
                      <div className="w-4 h-4 rounded-full bg-[hsl(90,52%,69%)]" />
                      <div className="w-4 h-4 rounded-full bg-[hsl(187,50%,35%)]" />
                      <div className="w-4 h-4 rounded-full bg-[hsl(43,81%,95%)]" />
                    </div>
                    <span className="text-sm font-medium">{t("settings.palette.brand")}</span>
                  </button>
                  <button
                    onClick={() => setColorPalette("neutral")}
                    className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-smooth ${
                      colorPalette === "neutral" ? "border-primary bg-primary/10" : "border-border"
                    }`}
                    data-testid="button-palette-neutral"
                  >
                    <div className="flex gap-1">
                      <div className="w-4 h-4 rounded-full bg-black" />
                      <div className="w-4 h-4 rounded-full bg-gray-500" />
                      <div className="w-4 h-4 rounded-full bg-white border border-gray-300" />
                    </div>
                    <span className="text-sm font-medium">{t("settings.palette.neutral")}</span>
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Profile link */}
          <Card className="animate-fade-in-up stagger-1">
            <CardContent className="py-4">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <Avatar className="h-10 w-10 shrink-0">
                    <AvatarImage src={user.profileImageUrl} alt={user.firstName || t("ui.userFallback")} />
                    <AvatarFallback>{getUserInitials()}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{user.firstName} {user.lastName}</p>
                    <p className="text-sm text-muted-foreground truncate">{user.email}</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="shrink-0" onClick={() => setLocation("/profile")}>
                  <User className="h-4 w-4 mr-2" /> {t("settings.profile")}
                </Button>
              </div>
            </CardContent>
          </Card>

          {(user.isSuperAdmin || user.plan !== "free") && (
            <Card ref={companyCardRef} className={`animate-fade-in-up stagger-2 transition-all duration-700 ${highlightCompany ? "ring-2 ring-primary ring-offset-2 shadow-lg" : ""}`}>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Building2 className="h-5 w-5" /> {t("settings.company")}
                    </CardTitle>
                    <CardDescription>{t("ui.manage_your_organization_and_team_members")}</CardDescription>
                  </div>
                  {companyData && canManageMembers && (
                    <Dialog open={showInviteMember} onOpenChange={setShowInviteMember}>
                      <DialogTrigger asChild>
                        <Button size="sm" data-testid="button-invite-member">
                          <UserPlus className="mr-2 h-4 w-4" /> {t("settingsx.inviteMember")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>{t("ui.invite_team_member")}</DialogTitle>
                          <DialogDescription>
                            {t("ui.add_a_new_member_to_your_organization")}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="invite-email">{t("ui.email_address")}</Label>
                            <Input
                              id="invite-email"
                              type="email"
                              placeholder="colleague@company.com"
                              value={inviteEmail}
                              onChange={(e) => setInviteEmail(e.target.value)}
                              data-testid="input-invite-email"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="invite-role">{t("ui.role")}</Label>
                            <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as CompanyRole)}>
                              <SelectTrigger data-testid="select-invite-role">
                                <SelectValue placeholder={t("ui.select_a_role")} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="admin">{t("settingsx.adminFull")}</SelectItem>
                                <SelectItem value="sender">{t("ui.sender_create_and_send_documents")}</SelectItem>
                                <SelectItem value="viewer">{t("ui.viewer_view_only")}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <DialogFooter>
                          <Button
                            onClick={() => inviteMemberMutation.mutate({ email: inviteEmail, role: inviteRole })}
                            disabled={!inviteEmail || inviteMemberMutation.isPending}
                            data-testid="button-submit-invite"
                          >
                            {inviteMemberMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {t("ui.send_invitation")}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {companyLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : companyData ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div>
                          <p className="font-medium">{companyData.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {companyData.members?.length || 1} member{(companyData.members?.length || 1) !== 1 ? "s" : ""}
                          </p>
                        </div>
                        {canManageMembers && (
                          <Dialog open={showEditCompany} onOpenChange={(open) => {
                            setShowEditCompany(open);
                            if (open) setEditCompanyName(companyData.name);
                          }}>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid="button-edit-company">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            </DialogTrigger>
                            <DialogContent>
                              <DialogHeader>
                                <DialogTitle>{t("ui.edit_company")}</DialogTitle>
                                <DialogDescription>
                                  {t("ui.update_your_organization_details")}
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div className="space-y-2">
                                  <Label htmlFor="edit-company-name">{t("ui.company_name")}</Label>
                                  <Input
                                    id="edit-company-name"
                                    placeholder={t("settingsx.companyPlaceholder")}
                                    value={editCompanyName}
                                    onChange={(e) => setEditCompanyName(e.target.value)}
                                    data-testid="input-edit-company-name"
                                  />
                                </div>
                              </div>
                              <DialogFooter>
                                <Button variant="outline" onClick={() => setShowEditCompany(false)}>
                                  {t("ui.cancel")}
                                </Button>
                                <Button
                                  onClick={() => updateCompanyMutation.mutate({ name: editCompanyName })}
                                  disabled={!editCompanyName || editCompanyName === companyData.name || updateCompanyMutation.isPending}
                                  data-testid="button-save-company"
                                >
                                  {updateCompanyMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                  {t("ui.save_changes")}
                                </Button>
                              </DialogFooter>
                            </DialogContent>
                          </Dialog>
                        )}
                      </div>
                      <Badge variant="secondary">{planLabel(companyData.plan, t)}</Badge>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                        <Users className="h-4 w-4" /> {t("company.teamMembers")}
                      </div>
                      <div className="divide-y rounded-lg border">
                        {companyData.members?.map((member: CompanyMember) => (
                          <div key={member.id} className="flex items-center justify-between gap-4 p-3" data-testid={`member-${member.userId}`}>
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="text-xs">
                                  {member.name?.[0]?.toUpperCase() || member.email?.[0]?.toUpperCase() || "?"}
                                </AvatarFallback>
                              </Avatar>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium truncate">
                                  {member.name || member.email}
                                </p>
                                <p className="text-sm text-muted-foreground truncate">{member.email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant={getRoleBadgeVariant(member.role)} className="flex items-center gap-1">
                                {getRoleIcon(member.role)}
                                {member.role}
                              </Badge>
                              {canManageMembers && member.role !== "owner" && member.userId !== user.id && (
                                <div className="flex items-center gap-1">
                                  <Select
                                    value={member.role}
                                    onValueChange={(v) => updateRoleMutation.mutate({ memberId: member.id, role: v as CompanyRole })}
                                  >
                                    <SelectTrigger className="h-8 w-24" data-testid={`select-role-${member.userId}`}>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="admin">{t("settingsx.admin")}</SelectItem>
                                      <SelectItem value="sender">{t("ui.sender")}</SelectItem>
                                      <SelectItem value="viewer">{t("ui.viewer")}</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeMemberMutation.mutate(member.id)}
                                    disabled={removeMemberMutation.isPending}
                                    data-testid={`button-remove-${member.userId}`}
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Documents are auto-assigned to company - no migration needed */}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="font-medium mb-2">{t("settingsx.noCompany")}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t("ui.create_a_company_to_collaborate_with_your_team")}
                    </p>
                    <Dialog open={showCreateCompany} onOpenChange={(open) => {
                      setShowCreateCompany(open);
                      if (!open) {
                        setCompanyName("");
                        setCompanyCnpj("");
                        setCompanyCountry("international");
                        setNewCompanyLogoFile(null);
                        setNewCompanyLogoPreview(null);
                      }
                    }}>
                      <DialogTrigger asChild>
                        <Button data-testid="button-create-company">
                          <Building2 className="mr-2 h-4 w-4" /> {t("ui.create_company")}
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-lg">
                        <DialogHeader>
                          <DialogTitle>{t("ui.create_company")}</DialogTitle>
                          <DialogDescription>
                            {t("ui.set_up_your_organization_to_invite_team_members")}
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          {/* Logo Upload */}
                          <div className="flex flex-col items-center gap-3">
                            <div
                              className="relative h-20 w-20 rounded-xl border-2 border-dashed border-muted-foreground/30 flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors overflow-hidden"
                              onClick={() => newLogoInputRef.current?.click()}
                            >
                              {newCompanyLogoPreview ? (
                                <img src={newCompanyLogoPreview} alt="Logo" className="h-full w-full object-cover" />
                              ) : (
                                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                                  <Camera className="h-6 w-6" />
                                  <span className="text-[10px]">Logo</span>
                                </div>
                              )}
                            </div>
                            <input
                              ref={newLogoInputRef}
                              type="file"
                              accept="image/png,image/jpeg,image/svg+xml"
                              className="hidden"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setNewCompanyLogoFile(file);
                                  const reader = new FileReader();
                                  reader.onload = () => setNewCompanyLogoPreview(reader.result as string);
                                  reader.readAsDataURL(file);
                                }
                              }}
                            />
                            <p className="text-xs text-muted-foreground">{t("settingsx.logoHint")}</p>
                          </div>

                          {/* Company Name */}
                          <div className="space-y-2">
                            <Label htmlFor="company-name">{t("ui.company_name_34")}</Label>
                            <Input
                              id="company-name"
                              placeholder={t("settingsx.companyPlaceholder")}
                              value={companyName}
                              onChange={(e) => setCompanyName(e.target.value)}
                              data-testid="input-company-name"
                            />
                          </div>

                          {/* Country Selection */}
                          <div className="space-y-2">
                            <Label>{t("ui.company_location")}</Label>
                            <div className="grid grid-cols-2 gap-3">
                              <button
                                type="button"
                                onClick={() => setCompanyCountry("brazil")}
                                className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                                  companyCountry === "brazil" ? "border-primary bg-primary/10" : "border-border"
                                }`}
                              >
                                <span className="text-lg">🇧🇷</span>
                                <span className="text-xs font-medium">{t("common.brazil")}</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => { setCompanyCountry("international"); setCompanyCnpj(""); }}
                                className={`p-3 rounded-lg border-2 flex flex-col items-center gap-1 transition-all ${
                                  companyCountry === "international" ? "border-primary bg-primary/10" : "border-border"
                                }`}
                              >
                                <Globe className="h-5 w-5" />
                                <span className="text-xs font-medium">{t("common.international")}</span>
                              </button>
                            </div>
                          </div>

                          {/* CNPJ (Brazil only) */}
                          {companyCountry === "brazil" && (
                            <div className="space-y-2">
                              <Label htmlFor="company-cnpj">CNPJ</Label>
                              <Input
                                id="company-cnpj"
                                placeholder="00.000.000/0000-00"
                                value={companyCnpj}
                                onChange={(e) => {
                                  // Auto-format CNPJ
                                  let v = e.target.value.replace(/\D/g, "").slice(0, 14);
                                  if (v.length > 12) v = v.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, "$1.$2.$3/$4-$5");
                                  else if (v.length > 8) v = v.replace(/^(\d{2})(\d{3})(\d{3})(\d{0,4})/, "$1.$2.$3/$4");
                                  else if (v.length > 5) v = v.replace(/^(\d{2})(\d{3})(\d{0,3})/, "$1.$2.$3");
                                  else if (v.length > 2) v = v.replace(/^(\d{2})(\d{0,3})/, "$1.$2");
                                  setCompanyCnpj(v);
                                }}
                                data-testid="input-company-cnpj"
                              />
                              <p className="text-xs text-muted-foreground">{t("settingsx.auditHint")}</p>
                            </div>
                          )}
                        </div>
                        <DialogFooter>
                          <Button variant="outline" onClick={() => setShowCreateCompany(false)}>
                            {t("ui.cancel")}
                          </Button>
                          <Button
                            onClick={() => createCompanyMutation.mutate(companyName)}
                            disabled={!companyName || createCompanyMutation.isPending}
                            data-testid="button-submit-company"
                          >
                            {createCompanyMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {t("ui.create_company")}
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {crmContext.data?.hasCompany && crmContext.data.isAdmin && (
            <Card ref={crmCardRef} className={`animate-fade-in-up stagger-2 transition-all duration-700 ${highlightCrm ? "ring-2 ring-primary ring-offset-2 shadow-lg" : ""}`} data-testid="card-crm-module">
              {crmConfirm}
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" /> {t("settings.crmModule")}
                </CardTitle>
                <CardDescription>{t("settings.crmModule.desc")}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
                  <div className="space-y-1">
                    <Label htmlFor="crm-module">{crmContext.data.enabled ? t("settings.crmModule.on") : t("settings.crmModule.off")}</Label>
                    <p className="text-sm text-muted-foreground">{t("settings.crmModule.adminOnly")}</p>
                  </div>
                  <Switch id="crm-module" checked={crmContext.data.enabled} disabled={crmSaving} onCheckedChange={toggleCrmModule} data-testid="switch-crm-module" />
                </div>
              </CardContent>
            </Card>
          )}

          {companyData && canManageMembers && (
            <Card className="animate-fade-in-up stagger-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-5 w-5" /> {t("settingsx.signaturePolicy")}
                </CardTitle>
                <CardDescription>
                  {t("ui.configure_how_your_team_members_can_sign_documen")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>{t("ui.company_location_31")}</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => handleCountryChange("brazil")}
                        className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-smooth ${
                          signatureCountry === "brazil" ? "border-primary bg-primary/10" : "border-border"
                        }`}
                        data-testid="button-country-brazil"
                      >
                        <span className="text-2xl">BR</span>
                        <span className="text-sm font-medium">{t("common.brazil")}</span>
                      </button>
                      <button
                        onClick={() => handleCountryChange("international")}
                        className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-smooth ${
                          signatureCountry === "international" ? "border-primary bg-primary/10" : "border-border"
                        }`}
                        data-testid="button-country-international"
                      >
                        <Globe className="h-6 w-6" />
                        <span className="text-sm font-medium">{t("common.international")}</span>
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>{t("ui.allowed_signature_methods")}</Label>
                    <p className="text-sm text-muted-foreground">{t("settingsx.methodsHint")}</p>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center h-10 w-10 rounded-md bg-blue-500/10">
                            <PenTool className="h-5 w-5 text-blue-500" />
                          </div>
                          <div>
                            <p className="font-medium">{t("ui.draw_signature")}</p>
                            <p className="text-sm text-muted-foreground">{t("settingsx.drawnHint")}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {defaultMethod === "draw" && <Badge variant="secondary">{t("ui.default")}</Badge>}
                          <Switch
                            checked={allowedMethods.includes("draw")}
                            onCheckedChange={(checked) => handleMethodToggle("draw", checked)}
                            data-testid="switch-method-draw"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center h-10 w-10 rounded-md bg-green-500/10">
                            <Shield className="h-5 w-5 text-green-500" />
                          </div>
                          <div>
                            <p className="font-medium">{t("ui.gov_br_style")}</p>
                            <p className="text-sm text-muted-foreground">{signatureCountry === "brazil" ? t("settings.govbrStyleDescRequired") : t("settings.govbrStyleDesc")}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {defaultMethod === "govbr" && <Badge variant="secondary">{t("ui.default")}</Badge>}
                          <Switch
                            checked={allowedMethods.includes("govbr")}
                            onCheckedChange={(checked) => handleMethodToggle("govbr", checked)}
                            data-testid="switch-method-govbr"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-between p-3 rounded-lg border">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center h-10 w-10 rounded-md bg-purple-500/10">
                            <Type className="h-5 w-5 text-purple-500" />
                          </div>
                          <div>
                            <p className="font-medium">{t("ui.typed_name")}</p>
                            <p className="text-sm text-muted-foreground">{t("settingsx.typedHint")}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {defaultMethod === "typed" && <Badge variant="secondary">{t("ui.default")}</Badge>}
                          <Switch
                            checked={allowedMethods.includes("typed")}
                            onCheckedChange={(checked) => handleMethodToggle("typed", checked)}
                            data-testid="switch-method-typed"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>{t("ui.default_signature_method")}</Label>
                    <Select value={defaultMethod} onValueChange={handleDefaultMethodChange}>
                      <SelectTrigger data-testid="select-default-method">
                        <SelectValue placeholder={t("ui.select_default_method")} />
                      </SelectTrigger>
                      <SelectContent>
                        {allowedMethods.includes("draw") && <SelectItem value="draw">{t("ui.draw_signature")}</SelectItem>}
                        {allowedMethods.includes("govbr") && <SelectItem value="govbr">{t("ui.gov_br_style")}</SelectItem>}
                        {allowedMethods.includes("typed") && <SelectItem value="typed">{t("ui.typed_name")}</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>

                  {signatureCountry === "brazil" && (
                    <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                      <div>
                        <p className="font-medium">{t("settingsx.requireCpf")}</p>
                        <p className="text-sm text-muted-foreground">
                          {t("ui.mandatory_for_brazilian_companies_using_gov_br_s")}
                        </p>
                      </div>
                      <Switch
                        checked={requireCpf}
                        onCheckedChange={handleRequireCpfChange}
                        data-testid="switch-require-cpf"
                      />
                    </div>
                  )}

                  <div className="pt-4 border-t">
                    <SigningDeadlineSetting
                      days={signatureExpirationDays}
                      disabled={updateSignaturePolicyMutation.isPending}
                      onSave={(days) => handleExpirationDaysChange(days)}
                    />
                  </div>

                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                          {language === "pt" ? "Exigir CPF na assinatura" : "Require CPF/ID on signature"}
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          {language === "pt" 
                            ? "Quando ativado, o signatário deve informar o CPF para assinar. Recomendado para empresas brasileiras."
                            : "When enabled, signers must provide their CPF/ID to sign. Recommended for Brazilian companies."}
                        </p>
                      </div>
                      <Switch
                        checked={requireCpf}
                        onCheckedChange={(checked) => {
                          setRequireCpf(checked);
                          updateCompanyMutation.mutate({ requireCpf: checked });
                        }}
                        data-testid="switch-require-cpf"
                      />
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-muted-foreground" />
                          {language === "pt" ? "Gerenciamento de assinatura GOV.BR" : "GOV.BR signature management"}
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          {language === "pt" 
                            ? "Quando ativado, permite enviar documentos para assinatura digital via portal GOV.BR (ICP-Brasil). O signatário receberá instruções para assinar no assinador.iti.br e enviar o documento assinado de volta."
                            : "When enabled, allows sending documents for digital signature via GOV.BR portal (ICP-Brasil). The signer will receive instructions to sign on assinador.iti.br and upload the signed document back."}
                        </p>
                      </div>
                      <Switch
                        checked={enableGovBrManagement}
                        onCheckedChange={(checked) => {
                          setEnableGovBrManagement(checked);
                          updateCompanyMutation.mutate({ enableGovBrManagement: checked });
                        }}
                        data-testid="switch-enable-govbr"
                      />
                    </div>
                  </div>

                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex items-center justify-between gap-4">
                      <div className="space-y-1">
                        <Label className="flex items-center gap-2">
                          <MessageCircle className="h-4 w-4 text-muted-foreground" />
                          {t("settings.whatsappResend")}
                        </Label>
                        <p className="text-sm text-muted-foreground">{t("settings.whatsappResend.desc")}</p>
                      </div>
                      <Switch
                        checked={whatsAppResendEnabled}
                        disabled={updateSignaturePolicyMutation.isPending}
                        onCheckedChange={(checked) => {
                          setWhatsAppResendEnabled(checked);
                          updateSignaturePolicyMutation.mutate({ whatsAppResendEnabled: checked }, {
                            onError: () => setWhatsAppResendEnabled(!checked),
                          });
                        }}
                        data-testid="switch-whatsapp-resend"
                      />
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <VerificationQrSetting
                      value={verificationQrMode}
                      disabled={updateSignaturePolicyMutation.isPending}
                      onChange={(mode) => {
                        const before = verificationQrMode;
                        setVerificationQrMode(mode);
                        updateSignaturePolicyMutation.mutate({ verificationQrMode: mode }, { onError: () => setVerificationQrMode(before) });
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {!companyData && personalQrMode && (
            <Card className="animate-fade-in-up stagger-3" data-testid="card-personal-qr">
              <CardContent className="pt-6">
                <VerificationQrSetting
                  value={personalQrMode}
                  onChange={async (mode) => {
                    const before = personalQrMode;
                    setPersonalQrMode(mode);
                    try {
                      const res = await apiRequest("PUT", "/api/account/settings", { verificationQrMode: mode });
                      if (!res.ok) throw new Error();
                      toast({ title: t("common.success"), description: t("settingsx.settingsUpdated") });
                    } catch {
                      setPersonalQrMode(before);
                      toast({ title: t("common.error"), variant: "destructive" });
                    }
                  }}
                />
              </CardContent>
            </Card>
          )}

          <Card ref={plansCardRef} className={`animate-fade-in-up stagger-4 transition-all duration-700 ${highlightPlans ? "ring-2 ring-primary ring-offset-2 shadow-lg" : ""}`} data-testid="card-plans">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" /> {t("settings.subscription")}
              </CardTitle>
              <CardDescription>{t("settings.subscription.desc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <RenewalControl subscription={currentSubscription} />
              {hasAnnual && (
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-testid="billing-cycle">
                  <div className="inline-flex rounded-full border bg-muted/40 p-1">
                    <button type="button" onClick={() => setBillingCycle("monthly")} className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${billingCycle === "monthly" ? "bg-background shadow" : "text-muted-foreground"}`} aria-pressed={billingCycle === "monthly"} data-testid="billing-cycle-monthly">
                      {language === "pt" ? "Mensal" : "Monthly"}
                    </button>
                    <button type="button" onClick={() => setBillingCycle("annual")} className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${billingCycle === "annual" ? "bg-background shadow" : "text-muted-foreground"}`} aria-pressed={billingCycle === "annual"} data-testid="billing-cycle-annual">
                      {language === "pt" ? "Anual" : "Annual"}
                      {maxSavings > 0 && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">{language === "pt" ? `economize até ${maxSavings}%` : `save up to ${maxSavings}%`}</span>}
                    </button>
                  </div>
                  {user.plan !== "free" && currentSubscription && (
                    <span className="text-xs text-muted-foreground" data-testid="current-cycle">
                      {language === "pt"
                        ? `Seu plano atual: ${currentCycle === "annual" ? "anual" : "mensal"}`
                        : `Your current plan: ${currentCycle}`}
                    </span>
                  )}
                </div>
              )}
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {plans.filter(p => p.isActive !== false || user.isSuperAdmin).map((plan) => (
                  <div
                    key={plan.name}
                    className={`p-4 rounded-lg border-2 ${plan.current ? "border-primary bg-primary/5" : plan.isActive === false ? "border-dashed border-muted-foreground/30 opacity-60" : "border-border"}`}
                    data-testid={`plan-${plan.id}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h3 className="font-semibold">{plan.name}</h3>
                      {plan.current && <Badge>{language === "pt" ? "Atual" : "Current"}</Badge>}
                      {plan.isActive === false && <Badge variant="outline" className="text-muted-foreground">{language === "pt" ? "Oculto" : "Hidden"}</Badge>}
                    </div>
                    <p className="text-2xl font-bold" data-testid={`plan-price-${plan.id}`}>{plan.price}<span className="text-sm font-normal text-muted-foreground">{plan.period}</span></p>
                    <p className="mb-3 min-h-[1.25rem] text-xs text-muted-foreground">
                      {plan.perMonth}
                      {billingCycle === "annual" && plan.annualAvailable && plan.savings > 0 && <span className="ml-1 text-emerald-700 dark:text-emerald-300">· {language === "pt" ? `${plan.savings}% off` : `${plan.savings}% off`}</span>}
                      {billingCycle === "annual" && !plan.annualAvailable && !plan.contactSales && plan.id !== "free" && (language === "pt" ? "Só mensal" : "Monthly only")}
                    </p>
                    <ul className="space-y-1 text-sm text-muted-foreground mb-4">
                      {plan.features.map((f: string, i: number) => {
                        const included = plan.featuresIncluded?.[i] !== false;
                        return (
                          <li key={f} className={`flex items-center gap-2 ${!included ? "opacity-40 line-through" : ""}`}>
                            {included ? <Check className="h-3 w-3 text-primary flex-shrink-0" /> : <X className="h-3 w-3 text-muted-foreground flex-shrink-0" />} {f}
                          </li>
                        );
                      })}
                    </ul>
                    {!plan.current && plan.id !== "free" && (
                      (() => {
                        const isDowngrade = plan.level < currentPlanLevel;
                        const canInteract = user.isSuperAdmin || !isDowngrade;
                        return canInteract ? (
                          <Button 
                            variant={plan.contactSales ? "outline" : "default"} 
                            className="w-full" 
                            size="sm" 
                            onClick={() => plan.contactSales ? setSalesDialogOpen(true) : handleUpgrade(plan.id, billingCycle === "annual" && plan.annualAvailable ? "annual" : "monthly")}
                            data-testid={`button-upgrade-${plan.id}`}
                          >
                            {plan.contactSales 
                              ? (language === "pt" ? "Falar com Vendas" : "Contact Sales")
                              : billingCycle === "annual" && plan.annualAvailable
                                ? (language === "pt" ? "Assinar plano anual" : "Subscribe yearly")
                                : (language === "pt" ? "Fazer Upgrade" : "Upgrade")}
                          </Button>
                        ) : (
                          <Button variant="outline" className="w-full opacity-50" size="sm" disabled>
                            {language === "pt" ? "Plano inferior" : "Lower plan"}
                          </Button>
                        );
                      })()
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>


          {/* SSO / SAML Configuration - Enterprise+ plans with SSO support */}
          {companyData && canManageMembers && (user.isSuperAdmin || user.plan === "enterprise" || user.plan === "customenterprise" || user.plan === "custom_enterprise") && (
            <Card className="animate-fade-in-up stagger-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="h-5 w-5" /> {t("settings.sso")}
                </CardTitle>
                <CardDescription>
                  {t("ui.allow_your_team_to_login_with_their_corporate_id")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center h-10 w-10 rounded-md bg-blue-500/10">
                      <Lock className="h-5 w-5 text-blue-500" />
                    </div>
                    <div>
                      <p className="font-medium">{t("settingsx.enableSso")}</p>
                      <p className="text-sm text-muted-foreground">{t("settingsx.ssoHint")}</p>
                    </div>
                  </div>
                  <Switch
                    checked={ssoEnabled}
                    onCheckedChange={(checked) => {
                      setSsoEnabled(checked);
                      updateCompanyMutation.mutate({ ssoEnabled: checked } as any);
                    }}
                  />
                </div>
                {ssoEnabled && (
                  <div className="space-y-4 p-4 rounded-lg border bg-muted/30">
                    <div className="space-y-2">
                      <Label>{t("ui.provider")}</Label>
                      <Select value={ssoProvider} onValueChange={(v) => setSsoProvider(v)}>
                        <SelectTrigger><SelectValue placeholder={t("ui.select_provider")} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="azure_ad">Azure AD (Microsoft Entra ID)</SelectItem>
                          <SelectItem value="saml">SAML 2.0</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {ssoProvider === "azure_ad" && (
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label>Azure Tenant ID</Label>
                          <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={azureTenantId} onChange={(e) => setAzureTenantId(e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label>Azure Client ID</Label>
                          <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" value={azureClientId} onChange={(e) => setAzureClientId(e.target.value)} />
                        </div>
                        <Button size="sm" onClick={() => {
                          updateCompanyMutation.mutate({ ssoProvider: "azure_ad", azureTenantId, azureClientId } as any);
                        }}>{t("ui.save_sso_configuration")}</Button>
                      </div>
                    )}
                    {ssoProvider === "saml" && (
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <Label>Entity ID</Label>
                          <Input placeholder="https://your-idp.com/entity" value={samlEntityId} onChange={(e) => setSamlEntityId(e.target.value)} />
                        </div>
                        <div className="space-y-2">
                          <Label>{t("settingsx.signOnUrl")}</Label>
                          <Input placeholder="https://your-idp.com/sso/saml" value={samlSignOnUrl} onChange={(e) => setSamlSignOnUrl(e.target.value)} />
                        </div>
                        <Button size="sm" onClick={() => {
                          updateCompanyMutation.mutate({ ssoProvider: "saml", samlEntityId, samlSignOnUrl } as any);
                        }}>{t("ui.save_saml_configuration")}</Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}


          {(user.isSuperAdmin || user.plan === "enterprise" || user.plan === "custom_enterprise" || user.plan === "customenterprise") && (
            <Card className="animate-fade-in-up stagger-5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" /> {t("settingsx.customBranding")}
                </CardTitle>
                <CardDescription>
                  {t("ui.customize_the_appearance_for_your_organization")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 sm:grid-cols-2">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>{t("ui.company_logo")}</Label>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        data-testid="input-logo-file"
                      />
                      <div className="flex items-center gap-4">
                        <div className="h-16 w-16 rounded-md border-2 border-dashed flex items-center justify-center text-muted-foreground overflow-hidden">
                          {companyLogo ? (
                            <img src={companyLogo} alt={t("settings.companyLogoAlt")} className="h-full w-full object-contain" />
                          ) : (
                            <Camera className="h-6 w-6" />
                          )}
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => logoInputRef.current?.click()}
                          disabled={uploadingLogo}
                          data-testid="button-upload-logo"
                        >
                          {uploadingLogo ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : null}
                          {companyLogo ? t("settings.changeLogo") : t("settings.uploadLogo")}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">{t("settingsx.logoSize")}</p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="brand-color">{t("ui.primary_brand_color")}</Label>
                      <div className="flex items-center gap-2">
                        <Input id="brand-color" type="color" className="w-12 h-9 p-1" defaultValue="#3b82f6" data-testid="input-brand-color" />
                        <Input placeholder="#3b82f6" defaultValue="#3b82f6" className="flex-1" data-testid="input-brand-color-hex" />
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="email-footer">{t("ui.email_footer_text")}</Label>
                      <Input id="email-footer" placeholder={t("settingsx.footerPlaceholder")} data-testid="input-email-footer" />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="custom-domain">{t("ui.custom_signing_domain")}</Label>
                      <Input id="custom-domain" placeholder="sign.yourcompany.com" data-testid="input-custom-domain" />
                      <p className="text-xs text-muted-foreground">{t("settingsx.customDomain")}</p>
                    </div>
                  </div>
                </div>
                <Button data-testid="button-save-branding">{t("settings.saveBranding")}</Button>
              </CardContent>
            </Card>
          )}

      {/* ── Integrações ── */}
      <Card className="mt-6" id="integrations">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Plug className="h-5 w-5" />{t("settings.integrations")}</CardTitle>
          <CardDescription>{t("settings.integrationsDesc")}</CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-3">
          {([
            ["/api-webhooks", KeyRound, t("settings.intApiTitle"), t("settings.intApiDesc")],
            ["/api-webhooks#webhooks", Webhook, t("settings.intWebhooksTitle"), t("settings.intWebhooksDesc")],
            ["/dynamics365", Building2, t("settings.intDynamicsTitle"), t("settings.intDynamicsDesc")],
          ] as Array<[string, typeof Plug, string, string]>).map(([href, Icon, title, desc]) => (
            <button key={href} type="button" onClick={() => navigateTo(href)} className="rounded-lg border p-4 text-left transition-colors hover:bg-muted/50" data-testid={`tile-${href.replace(/[^a-z0-9]/gi, "")}`}>
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary"><Icon className="h-4 w-4" /></span>
              <div className="mt-3 font-medium">{title}</div>
              <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
            </button>
          ))}
        </CardContent>
      </Card>

          {/* Email Templates Section */}
          <Card className="animate-fade-in-up stagger-6">
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2 flex-wrap">
                <span className="flex items-center gap-2">
                  <Mail className="h-5 w-5" /> {t("ui.email_templates")}
                </span>
                <Button 
                  size="sm" 
                  onClick={() => {
                    setEditingTemplate(null);
                    resetTemplateForm();
                    setShowEmailTemplateEditor(true);
                  }}
                  data-testid="button-new-email-template"
                >
                  <Plus className="h-4 w-4 mr-1" /> {t("settingsx.newTemplate")}
                </Button>
              </CardTitle>
              <CardDescription>
                {t("ui.customize_email_notifications_sent_to_signers")}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {templatesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : emailTemplates && emailTemplates.length > 0 ? (
                <div className="space-y-3">
                  {emailTemplates.map((template) => (
                    <div 
                      key={template.id} 
                      className="flex items-center justify-between gap-4 p-3 rounded-md border"
                      data-testid={`template-row-${template.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-medium text-sm truncate">{template.name}</p>
                          <Badge variant="secondary" className="text-xs">
                            {getTemplateTypeLabel(template.templateType)}
                          </Badge>
                          {template.isDefault && (
                            <Badge variant="outline" className="text-xs">{t("ui.default")}</Badge>
                          )}
                          {!template.isActive && (
                            <Badge variant="destructive" className="text-xs">{t("status.inactive")}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate mt-1">{template.subject}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditTemplate(template)}
                          data-testid={`button-edit-template-${template.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteTemplateMutation.mutate(template.id)}
                          disabled={deleteTemplateMutation.isPending}
                          data-testid={`button-delete-template-${template.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">{t("settingsx.noTemplates")}</p>
                  <p className="text-xs mt-1">{t("settingsx.defaultTemplates")}</p>
                </div>
              )}

              <div className="pt-4 border-t space-y-2">
                <p className="text-sm font-medium">{t("settings.availableVariables")}</p>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="font-mono text-xs">{"{{recipientName}}"}</Badge>
                  <Badge variant="outline" className="font-mono text-xs">{"{{senderName}}"}</Badge>
                  <Badge variant="outline" className="font-mono text-xs">{"{{documentName}}"}</Badge>
                  <Badge variant="outline" className="font-mono text-xs">{"{{signatureUrl}}"}</Badge>
                  <Badge variant="outline" className="font-mono text-xs">{"{{message}}"}</Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {t("ui.use_these_placeholders_in_your_template_and_they")}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Super Admin Section - Only visible to super admins */}
          {user?.isSuperAdmin && (
            <SuperAdminSection />
          )}
        </div>
        </div>

        {/* Email Template Editor Dialog */}
        <Dialog open={showEmailTemplateEditor} onOpenChange={setShowEmailTemplateEditor}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTemplate ? t("settings.editEmailTemplate") : t("settings.createEmailTemplate")}</DialogTitle>
              <DialogDescription>
                {t("settings.emailTemplateDesc")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="template-name">{t("ui.template_name")}</Label>
                  <Input
                    id="template-name"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder={t("ui.my_custom_template")}
                    data-testid="input-template-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="template-type">{t("ui.template_type")}</Label>
                  <Select
                    value={templateType}
                    onValueChange={(v) => setTemplateType(v as EmailTemplate["templateType"])}
                    disabled={!!editingTemplate}
                  >
                    <SelectTrigger id="template-type" data-testid="select-template-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="signature_request">{t("ui.signature_request")}</SelectItem>
                      <SelectItem value="signature_request_govbr">{t("ui.gov_br_signature_request")}</SelectItem>
                      <SelectItem value="signature_completed">{t("ui.signature_completed")}</SelectItem>
                      <SelectItem value="signature_reminder">{t("ui.signature_reminder")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="template-subject">{t("ui.email_subject")}</Label>
                <Input
                  id="template-subject"
                  value={templateSubject}
                  onChange={(e) => setTemplateSubject(e.target.value)}
                  placeholder={t("settings.emailSubjectPlaceholder")}
                  data-testid="input-template-subject"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="template-body">{t("ui.email_body_html")}</Label>
                <Textarea
                  id="template-body"
                  value={templateBody}
                  onChange={(e) => setTemplateBody(e.target.value)}
                  placeholder={t("settings.emailBodyPlaceholder")}
                  className="min-h-[200px] font-mono text-sm"
                  data-testid="textarea-template-body"
                />
                <p className="text-xs text-muted-foreground">
                  {t("settings.emailBodyHelp")}
                </p>
              </div>

              <div className="flex items-center gap-2">
                <Switch
                  id="template-default"
                  checked={templateIsDefault}
                  onCheckedChange={setTemplateIsDefault}
                  data-testid="switch-template-default"
                />
                <Label htmlFor="template-default" className="cursor-pointer">
                  {t("ui.set_as_default_template_for_this_type")}
                </Label>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowEmailTemplateEditor(false);
                  setEditingTemplate(null);
                  resetTemplateForm();
                }}
                data-testid="button-cancel-template"
              >
                {t("ui.cancel")}
              </Button>
              <Button
                onClick={handleSaveTemplate}
                disabled={createTemplateMutation.isPending || updateTemplateMutation.isPending}
                data-testid="button-save-template"
              >
                {(createTemplateMutation.isPending || updateTemplateMutation.isPending) && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {editingTemplate ? t("settings.saveChanges") : t("settings.createTemplate")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Document Migration Dialog */}
        <Dialog open={showMigrateDocuments} onOpenChange={setShowMigrateDocuments}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{t("ui.migrate_documents_to_company")}</DialogTitle>
              <DialogDescription>
                {t("ui.select_which_personal_documents_you_d_like_to_mo")}
                {t("ui.this_will_make_them_accessible_to_all_team_membe")}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {personalDocsData && personalDocsData.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  <div className="flex items-center justify-between pb-2 border-b">
                    <Label className="text-sm">{t("settingsx.selectToMigrate")}</Label>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => {
                        if (selectedDocsToMigrate.length === personalDocsData.length) {
                          setSelectedDocsToMigrate([]);
                        } else {
                          setSelectedDocsToMigrate(personalDocsData.map(d => d.id));
                        }
                      }}
                      data-testid="button-toggle-all-docs"
                    >
                      {selectedDocsToMigrate.length === personalDocsData.length ? t("settings.deselectAll") : t("settings.selectAll")}
                    </Button>
                  </div>
                  {personalDocsData.map((doc) => (
                    <div 
                      key={doc.id} 
                      className="flex items-center gap-3 p-2 rounded-md hover-elevate cursor-pointer"
                      onClick={() => {
                        if (selectedDocsToMigrate.includes(doc.id)) {
                          setSelectedDocsToMigrate(selectedDocsToMigrate.filter(id => id !== doc.id));
                        } else {
                          setSelectedDocsToMigrate([...selectedDocsToMigrate, doc.id]);
                        }
                      }}
                      data-testid={`doc-row-${doc.id}`}
                    >
                      <Checkbox 
                        checked={selectedDocsToMigrate.includes(doc.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedDocsToMigrate([...selectedDocsToMigrate, doc.id]);
                          } else {
                            setSelectedDocsToMigrate(selectedDocsToMigrate.filter(id => id !== doc.id));
                          }
                        }}
                        data-testid={`checkbox-doc-${doc.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate text-sm">{doc.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.status} - Created {new Date(doc.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">{t("settingsx.nothingToMigrate")}</p>
              )}
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowMigrateDocuments(false)} data-testid="button-cancel-migrate">
                {t("ui.keep_as_personal")}
              </Button>
              <Button 
                onClick={() => migrateDocumentsMutation.mutate(selectedDocsToMigrate)}
                disabled={selectedDocsToMigrate.length === 0 || migrateDocumentsMutation.isPending}
                data-testid="button-confirm-migrate"
              >
                {migrateDocumentsMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("settings.migrateDocs").replace("{n}", String(selectedDocsToMigrate.length))}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


      {/* ── Modal Falar com Vendas ── */}
      <Dialog open={salesDialogOpen} onOpenChange={setSalesDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{language === "pt" ? "Falar com Vendas" : "Contact Sales"}</DialogTitle>
            <DialogDescription>
              {language === "pt"
                ? "Conte o que você precisa. Nossa equipe analisa e retorna no e-mail informado em até 1 dia útil."
                : "Tell us what you need. Our team will review and reply to the email below within 1 business day."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="sales-email">{t("ui.e_mail")}</Label>
              <Input id="sales-email" type="email" value={salesEmail} onChange={(e) => setSalesEmail(e.target.value)} placeholder="voce@empresa.com" data-testid="input-sales-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="sales-message">{language === "pt" ? "Mensagem" : "Message"}</Label>
              <Textarea id="sales-message" rows={5} value={salesMessage} onChange={(e) => setSalesMessage(e.target.value)} placeholder={language === "pt" ? "Volume de documentos, integrações, requisitos..." : "Document volume, integrations, requirements..."} data-testid="input-sales-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSalesDialogOpen(false)} disabled={salesSending}>
              {language === "pt" ? "Cancelar" : "Cancel"}
            </Button>
            <Button onClick={handleSendSalesContact} disabled={salesSending} data-testid="button-send-sales">
              {salesSending ? (language === "pt" ? "Enviando..." : "Sending...") : (language === "pt" ? "Enviar" : "Send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
